import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ActionsMenuBar.css';

export interface ActionItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'danger';
  disabled?: boolean;
}

export interface SelectionSummary {
  totalAmount?: number;
  paidAmount?: number;
  unpaidBonusAmount?: number;
  remainingAmount?: number;
  totalHours?: number;
  hasFinancialAccess?: boolean;
  /** id пользователя, когда выделены смены только одного пользователя */
  selectedUserId?: number;
  /** сумма нераспределённых авансов этого пользователя (подгружается при выделении) */
  unallocatedAdvancesSum?: number | null;
  /** идёт загрузка суммы нераспределённых авансов */
  unallocatedAdvancesLoading?: boolean;
}

export interface ActionsMenuBarProps {
  isOpen: boolean;
  actions: ActionItem[];
  onClose: () => void;
  onCancel?: () => void; // Новый пропс для кнопки отмены выделения
  className?: string;
}

const ActionsMenuBar: React.FC<ActionsMenuBarProps> = ({
  isOpen,
  actions,
  onClose,
  onCancel,
  className = ''
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = React.useState(false);
  const closeTimerRef = useRef<number | null>(null);
  /** Флаг: действие уже выполнено по touchend — не дублировать по click (мгновенная реакция на тап без задержки) */
  const handledByTouchRef = useRef(false);
  const activePressedRef = useRef<{ actionId: string; el: HTMLElement | null } | null>(null);
  const lastActionTimeRef = useRef<number>(0);

  const handleClose = useCallback(() => {
    // Помечаем закрытие для анимации, затем закрываем после короткой задержки
    setIsClosing(true);
    // Длина анимации в CSS ~60ms, ставим небольшой запас
    const timeout = 120;
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    // Сразу уменьшаем общую высоту снизу до высоты только статус-бара,
    // чтобы статус-бар начал уезжать одновременно с меню кнопок.
    try {
      const statusBarHeightValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--selection-status-bar-height')
        .trim();
      const statusBarHeightNum = statusBarHeightValue
        ? parseFloat(statusBarHeightValue.replace('px', ''))
        : 0;
      // Устанавливаем высоту только для статус-бара — меню кнопок начнёт прятаться по анимации
      document.documentElement.style.setProperty('--bottom-elements-height', `${statusBarHeightNum}px`);
    } catch (err) {
      // ignore
    }

    closeTimerRef.current = window.setTimeout(() => {
      onClose();
      closeTimerRef.current = null;
    }, timeout);
  }, [onClose]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  // Обработка Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  const handleActionClick = useCallback((action: ActionItem) => {
    if (!action.disabled) {
      // сначала уводим меню на задний план (z-index), чтобы sheet был виден сразу
      // и выполняем действие асинхронно, чтобы закрытие меню применилось мгновенно
      handleClose();
      queueMicrotask(() => {
        try {
          const now = Date.now();
          const timerLabel = `ActionsMenuBar:${action.id}:${now}`;
          console.debug(`[ActionsMenuBar] start ${timerLabel}`);
          console.time(timerLabel);
          const res: any = action.onClick();
          if (res && typeof res.then === 'function') {
            res.then(() => {
              console.timeEnd(timerLabel);
              console.debug(`[ActionsMenuBar] end ${timerLabel}`);
            }).catch((err: any) => {
              console.timeEnd(timerLabel);
              console.error(`[ActionsMenuBar] error ${timerLabel}`, err);
            });
          } else {
            console.timeEnd(timerLabel);
            console.debug(`[ActionsMenuBar] end ${timerLabel}`);
          }
        } catch (err) {
          console.error(err);
        }
      });
    }
  }, [handleClose]);

  /** Мгновенное срабатывание по touchend (без ожидания синтетического click и без задержки долгого нажатия) */
  const handleActionTouchEnd = useCallback((e: React.TouchEvent, action: ActionItem) => {
    if (action.disabled) return;
    // Считываем координаты старта с элемента (установлены в touchstart/onPointerDown)
    const el = e.currentTarget as HTMLElement;
    const touch = e.changedTouches[0];
    const startX = parseFloat(el.getAttribute('data-press-x') || '0');
    const startY = parseFloat(el.getAttribute('data-press-y') || '0');
    el.removeAttribute('data-press-x');
    el.removeAttribute('data-press-y');
    activePressedRef.current = null;

    const dx = Math.abs((touch?.clientX || 0) - startX);
    const dy = Math.abs((touch?.clientY || 0) - startY);
    const moved = Math.sqrt(dx * dx + dy * dy) > 10;
    if (moved) {
      // Пользователь скроллил — не выполнять действие
      handledByTouchRef.current = false;
      return;
    }

    const now = Date.now();
    if (now - lastActionTimeRef.current < 350) return;
    lastActionTimeRef.current = now;
    handledByTouchRef.current = true;
    console.debug('[ActionsMenuBar] touchend -> action', action.id, Date.now());
    handleActionClick(action);
    setTimeout(() => { handledByTouchRef.current = false; }, 300);
  }, [handleActionClick]);

  /** Немедленное срабатывание по pointerdown для мгновенной реакции на всех устройствах */
  const handleActionPointerDown = useCallback((e: React.PointerEvent, action: ActionItem) => {
    if (action.disabled) return;
    // Записываем стартовые координаты для последующей проверки на перемещение (скролл)
    const el = e.currentTarget as HTMLElement;
    el.setAttribute('data-press-x', String(e.clientX));
    el.setAttribute('data-press-y', String(e.clientY));
    activePressedRef.current = { actionId: action.id, el };
    // не блокируем событие, чтобы позволить скролл
  }, []);

  const handleActionPointerUp = useCallback((e: React.PointerEvent, action: ActionItem) => {
    if (action.disabled) return;
    const el = e.currentTarget as HTMLElement;
    const startX = parseFloat(el.getAttribute('data-press-x') || '0');
    const startY = parseFloat(el.getAttribute('data-press-y') || '0');
    el.removeAttribute('data-press-x');
    el.removeAttribute('data-press-y');
    activePressedRef.current = null;

    const dx = Math.abs(e.clientX - startX);
    const dy = Math.abs(e.clientY - startY);
    const moved = Math.sqrt(dx * dx + dy * dy) > 10; // порог скролла
    if (moved) return;

    const now = Date.now();
    if (now - lastActionTimeRef.current < 350) return; // защита от двойного нажатия
    lastActionTimeRef.current = now;
    handleActionClick(action);
  }, [handleActionClick]);

  /** Избегаем двойного вызова: click после touch уже обработан в touchend */
  const handleActionClickWithTouchCheck = useCallback((e: React.MouseEvent, action: ActionItem) => {
    if (handledByTouchRef.current) {
      e.preventDefault();
      e.stopPropagation();
      handledByTouchRef.current = false;
      return;
    }
    handleActionClick(action);
  }, [handleActionClick]);

  const handleCancelTouchEnd = useCallback((e: React.TouchEvent) => {
    // Simplified: use onClick only for cancel (handled elsewhere)
  }, [onCancel, handleClose]);

  const handleCancelPointerDown = useCallback((e: React.PointerEvent) => {
    // Simplified: use onClick only for cancel (handled elsewhere)
  }, []);

  const handleCancelPointerUp = useCallback((e: React.PointerEvent) => {
    // Simplified: use onClick only for cancel (handled elsewhere)
  }, [onCancel, handleClose]);

  const handleCancelClick = useCallback((e: React.MouseEvent) => {
    if (handledByTouchRef.current) {
      e.preventDefault();
      e.stopPropagation();
      handledByTouchRef.current = false;
      return;
    }
    console.debug('[ActionsMenuBar] click -> cancel', Date.now());
    onCancel?.();
    handleClose();
  }, [onCancel, handleClose]);

  // Устанавливаем CSS переменную для высоты меню действий и рендерим в общий контейнер снизу
  useEffect(() => {
    // Ensure bottom container exists
    const containerId = 'bottom-elements-root';
    let bottomRoot = document.getElementById(containerId);
    if (!bottomRoot) {
      bottomRoot = document.createElement('div');
      bottomRoot.id = containerId;
      document.body.appendChild(bottomRoot);
    }

    if (!isOpen) {
      // Очищаем CSS переменную при закрытии
      document.documentElement.style.setProperty('--actions-menu-bar-height', '0px');
      const statusBarHeightValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--selection-status-bar-height')
        .trim();
      const statusBarHeightNum = statusBarHeightValue
        ? parseFloat(statusBarHeightValue.replace('px', ''))
        : 0;
      document.documentElement.style.setProperty('--bottom-elements-height', `${statusBarHeightNum}px`);
      bottomRoot?.classList.remove('bottom-elements-open');
      bottomRoot?.classList.remove('bottom-elements-closing');
      return;
    }

    bottomRoot?.classList.add('bottom-elements-open');

    const updateHeight = () => {
      if (menuRef.current) {
        const rect = menuRef.current.getBoundingClientRect();
        const height = rect.height;

        const statusBarHeightValue = getComputedStyle(document.documentElement)
          .getPropertyValue('--selection-status-bar-height')
          .trim();
        const statusBarHeightNum = statusBarHeightValue
          ? parseFloat(statusBarHeightValue.replace('px', ''))
          : 0;

        const totalHeight = height + statusBarHeightNum;

        if (height > 0) {
          document.documentElement.style.setProperty('--actions-menu-bar-height', `${height}px`);
        } else {
          document.documentElement.style.setProperty('--actions-menu-bar-height', '0px');
        }

        if (totalHeight > 0) {
          document.documentElement.style.setProperty('--bottom-elements-height', `${totalHeight}px`);
        } else {
          document.documentElement.style.setProperty('--bottom-elements-height', `${statusBarHeightNum}px`);
        }
      }
    };

    updateHeight();
    const rafId = requestAnimationFrame(() => {
      updateHeight();
      setTimeout(updateHeight, 50);
      setTimeout(updateHeight, 200);
    });

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (menuRef.current) {
      resizeObserver.observe(menuRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      if (menuRef.current) {
        resizeObserver.unobserve(menuRef.current);
      }
      bottomRoot?.classList.remove('bottom-elements-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Проверяем, есть ли fullscreen модальное окно
  const fullscreenContainer = document.getElementById('fullscreen-card-bottom-elements');
  const container = fullscreenContainer || document.body;

  return createPortal(
    <div 
      ref={menuRef}
      className={`actions-menu-bar ${isClosing ? 'closing' : ''} ${fullscreenContainer ? 'in-fullscreen' : ''} ${className}`}
    >
        <div 
          ref={scrollContainerRef}
          className="actions-menu-bar-scroll"
        >
          {onCancel && (
            <button
              type="button"
              className="actions-menu-bar-cancel"
              onClick={handleCancelClick}
              title="Отменить выделение"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
          {actions.map((action) => (
            <button
              type="button"
              key={action.id}
              className={`actions-menu-bar-item actions-menu-bar-item--${action.variant || 'primary'} ${action.disabled ? 'disabled' : ''}`}
              onClick={(e) => handleActionClickWithTouchCheck(e, action)}
              onTouchStart={(e) => {
                const t = e.touches[0];
                const el = e.currentTarget as HTMLElement;
                el.setAttribute('data-press-x', String(t.clientX));
                el.setAttribute('data-press-y', String(t.clientY));
                activePressedRef.current = { actionId: action.id, el };
              }}
              onTouchEnd={(e) => handleActionTouchEnd(e, action)}
              onPointerDown={(e) => handleActionPointerDown(e, action)}
              onPointerUp={(e) => handleActionPointerUp(e, action)}
              disabled={action.disabled}
              title={action.label}
            >
              {action.icon && (
                <i className={action.icon}></i>
              )}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
    </div>,
    container
  );
};

export default ActionsMenuBar;

